import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { styles } from 'gipsy-misc'
import BigCalendar from 'react-big-calendar'
import moment from 'moment'
import Toolbar from './toolbar'
import 'react-big-calendar/lib/css/react-big-calendar.css'

import {
  CalendarItemDayContent,
  CalendarItemContent,
  TimeGutterHeader,
  TimeSlotWrapper,
  CalendarItemWeekContent
} from './components'

import { eventPropGetter, timeSlotPropGetter } from './getters'

const localizer = BigCalendar.momentLocalizer(moment)

class Calendar extends PureComponent {
  static propTypes = {
    className: PropTypes.string.isRequired,
    calendarItems: PropTypes.array.isRequired,
    date: PropTypes.instanceOf(Date).isRequired,
    scrollToTime: PropTypes.instanceOf(Date).isRequired,
    onChangeDate: PropTypes.func.isRequired,
    onSelectCalendarItem: PropTypes.func.isRequired,
    view: PropTypes.string.isRequired,
    onChangeView: PropTypes.func.isRequired,
    onCompleteTask: PropTypes.func.isRequired,
    onSelectSlot: PropTypes.func.isRequired,
    weeklyWorkingHours: PropTypes.object
  }
  static defaultProps = {
    className: ''
  }
  render() {
    return (
      <Container className={'gipsy-calendar ' + this.props.className} view={this.props.view}>
        <BigCalendar
          ref={ref => (this.calendar = ref)}
          selectable={true}
          localizer={localizer}
          events={this.props.calendarItems}
          views={[BigCalendar.Views.MONTH, BigCalendar.Views.WEEK, BigCalendar.Views.DAY]}
          view={this.props.view}
          onView={this.props.onChangeView}
          step={30}
          timeslots={1}
          showMultiDayTimes
          date={this.props.date}
          onNavigate={this.props.onChangeDate}
          onSelectEvent={this.props.onSelectCalendarItem}
          onSelectSlot={this.props.onSelectSlot}
          eventPropGetter={eventPropGetter}
          slotPropGetter={(date, resourceId) => timeSlotPropGetter(date, resourceId, this.props.weeklyWorkingHours)}
          formats={{
            timeGutterFormat: 'h:mm a'
          }}
          scrollToTime={this.props.scrollToTime}
          components={{
            event: CalendarItemContent,
            timeSlotWrapper: TimeSlotWrapper,
            timeGutterHeader: TimeGutterHeader,
            toolbar: Toolbar,
            day: {
              event: props => <CalendarItemDayContent {...props} onComplete={this.props.onCompleteTask} />
            },
            week: {
              event: props => <CalendarItemWeekContent {...props} />
            }
          }}
        />
      </Container>
    )
  }
}

export default Calendar

const AllDayRowCss = props => css`
  .rbc-time-header {
    padding: 0px;
  }
  .rbc-time-header-gutter {
    width: 80px !important;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .rbc-allday-cell {
    .rbc-row-content {
      padding: 5px;
    }
    .rbc-row {
      ${props =>
        props.view === BigCalendar.Views.DAY &&
        css`
          display: inline-block;
          vertical-align: top;
          margin-right: 4px;
          .rbc-event-allday {
            width: auto;
            display: inline-block;
          }
        `}
      .rbc-event-allday {
        padding: 5px 8px;
        border-radius: 4px;
        font-size: ${styles.fonts.fontSizeXSmall};
      }
    }
  }
`

const borderCss = props => css`
  // calendar container
  .rbc-time-view {
    border: none;
  }

  .rbc-time-header-content {
    border-left: 1px solid ${styles.colors.lightGrey};
  }

  // day view : all day row
  ${props =>
    props.view === BigCalendar.Views.DAY &&
    css`
      .rbc-time-header-content {
        border-left: none;
      }
    `}

  // all day column border (week / month view)
  .rbc-allday-cell .rbc-row-bg .rbc-day-bg + .rbc-day-bg {
    border-left: 1px solid ${styles.colors.lightGrey};
  }

  // week view : date header column border
  .rbc-header + .rbc-header {
    border-left: 1px solid ${styles.colors.lightGrey};
  }

  // month view : row border top
  .rbc-month-row + .rbc-month-row {
    border-top: 1px solid ${styles.colors.lightGrey};
  }

  .rbc-day-bg + .rbc-day-bg {
    border-left: 1px solid ${styles.colors.lightGrey};
  }

  // any cell
  .rbc-timeslot-group {
    border-left: none;
    border-bottom: 1px solid ${styles.colors.lightGrey};
  }
  .rbc-time-content {
    border-top: 1px solid ${styles.colors.lightGrey};
  }
  .rbc-time-content > * + * > * {
    border-left: none;
    border-bottom: 1px solid ${styles.colors.lightGrey};
  }

  .rbc-events-container {
    border-left: 1px solid ${styles.colors.lightGrey};
  }

  // calendar column border for day view
  ${props.view === BigCalendar.Views.DAY &&
    css`
      .rbc-day-slot .rbc-events-container {
        border-left: none;
      }
    `}

  .rbc-time-header.rbc-overflowing {
    border-right: none;
  }
`

const Container = styled.div`
  @keyframes calendarItemAppearAnimation {
    0% {
      transform: translateY(-40px);
      opacity: 0.5;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  height: 100%;
  // date header (week view)
  .rbc-header {
    font-weight: 500;
    padding: 5px;
    height: 30px;
  }
  .rbc-month-row {
    flex-basis: initial !important;
    min-height: 150px;
  }
  .rbc-timeslot-group {
    min-height: 50px;
  }
  .rbc-time-column {
    width: 80px;
  }
  .rbc-today {
    background-color: transparent;
  }

  .rbc-day-slot .rbc-event {
    border: 1px solid #efefef !important;
  }

  .rbc-day-slot .rbc-event-label {
    line-height: 16px;
    padding-right: 15px;
    display: none;
  }

  .rbc-day-slot .rbc-event-content {
    position: relative;
    width: auto;
    flex: none;
    height: auto;
    line-height: 16px;
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .rbc-time-content {
    height: 600px;
    & > .rbc-day-slot {
      width: 100% !important;
    }
  }

  ${props => AllDayRowCss(props)}
  ${props => borderCss(props)}
  .rbc-current-time-indicator {
    left: -80px;
    border-bottom: 2px solid ${styles.colors.primaryColor};
  }
`



// WEBPACK FOOTER //
// ./src/features/calendar/index.js